import React, { useState, useEffect, useRef } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../styles/AdjustmentInputs.css';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormHelperText,
  Snackbar,
  Alert,
  Grid,
  LinearProgress,
  CircularProgress } from
'@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import api from '../services/api';
import { adjustmentTypeToRouteMap, getColumnsForAdjustmentType } from '../utils/adjustmentTypeUtils';
import SearchableDataTable from '../components/SearchableDataTable';
import { useAuth } from '../hooks/useAuth';

import { useAdjustmentType, useScenario, useTagFilter, useSubtagFilter, useEntityFilter } from '../components/layout/Layout';

// Helper to format numbers with commas for display
const formatNumberWithCommas = (value) => {
  if (!value) return '';
  // Remove any existing commas and non-numeric characters except decimal point and minus sign
  const numericValue = value.toString().replace(/[^\d.-]/g, '');
  // Split into integer and decimal parts
  const parts = numericValue.split('.');
  // Add commas to integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Return formatted number
  return parts.join('.');
};

// Helper to remove commas from formatted number
const removeCommas = (value) => {
  if (!value) return '';
  return value.toString().replace(/,/g, '');
};

// Helper to get Activity Type options based on adjustment type
const getActivityTypeOptions = (adjustmentType) => {
  // Permanent types that can use CurrentActivityEQ
  const permanentTypes = ['Permanent', 'GS Permanent', 'TI Permanent', 'State Pre-App Perm', 'State Post-App Perm'];

  // Group 1: Current activity types (PL only, except for permanent types)
  const group1 = [
  'Pretax',
  'Permanent',
  'Payable',
  'GS Permanent',
  'TI Permanent',
  'Tax Adj. Return',
  'Tax Adj. Provision',
  'Tax Adj. RTP',
  'State Pre-App Perm',
  'State Post-App Perm',
  'State Tax Adj. Return',
  'State Tax Adj. Provision',
  'State Tax Adj. RTP'];


  // Group 2: Deferred/balance sheet activity types
  const group2 = [
  'Temporary',
  'GS Temporary',
  'TI Temporary',
  'Tax Effected Temporary',
  'Valuation Allowance',
  'Net Operating Loss',
  'Tax Effected Valuation Allowance',
  'State Pre-App Temp',
  'State Post-App Temp',
  'State Tax Effected Temp',
  'State Pre-App NOL',
  'State Post-App NOL',
  'State Pre-App Valuation Allowance',
  'State Post-App Valuation Allowance',
  'State Tax Effected Valuation Allowance'];


  if (!adjustmentType) return [];

  if (group1.includes(adjustmentType)) {
    // Only Permanent, GS Permanent, TI Permanent, and State Permanent types can use CurrentActivityEQ
    if (permanentTypes.includes(adjustmentType)) {
      return ['CurrentActivityPL', 'CurrentActivityEQ'];
    }
    // All other group1 types only get CurrentActivityPL
    return ['CurrentActivityPL'];
  }

  if (group2.includes(adjustmentType)) {
    return [
    'BeginningBalance',
    'ReturnToProvision',
    'DeferredTrueUp',
    'PurchaseAccounting',
    'CurrentActivityPL',
    'OCIAdjustment',
    'DeferredExpAdjustment',
    'BalanceSheetAdjustment',
    'CustomDeferredExpAdjustment',
    'CustomBalanceSheetAdjustment'];

  }

  return [];
};

// Tag and SubTag options - stored on frontend as temporary solution
const TAG_OPTIONS = [
'ORDINARY',
'DISCRETE',
'RTP',
'AUDIT',
'ACT',
'OCI',
'PPA',
'Deferred Only',
'Deferred Only PY',
'Balance Sheet Only',
'Balance Sheet Only PY',
'Valuation Allowance',
'Valuation Allowance PY',
'Uncertain Tax Position',
'Uncertain Tax Position PY'];


const SUBTAG_OPTIONS = [
'Import',
'Auto',
'Manual',
'Workpaper',
'Q1',
'Q2',
'Q3',
'Q4',
'2024',
'2023',
'2022'];


const AdjustmentInputs = () => {
  // State for adjustments
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [columns, setColumns] = useState([]);

  // State for master entities
  const [masterEntities, setMasterEntities] = useState([]);
  const [filteredEntities, setFilteredEntities] = useState([]);
  const [entityInputs, setEntityInputs] = useState([]);
  const [entityName, setEntityName] = useState('');
  const [entityLoading, setEntityLoading] = useState(false);

  // State inputs for FilingCode dropdown (filtered by EntityCode)
  const [stateInputsForFiling, setStateInputsForFiling] = useState([]);

  // State for master adjustments
  const [masterAdjustments, setMasterAdjustments] = useState([]);
  const [adjustmentName, setAdjustmentName] = useState('');
  const [adjustmentLoading, setAdjustmentLoading] = useState(false);

  // Get period filter from context


  // Get adjustment type filter from context
  const { selectedAdjustmentType, setSelectedAdjustmentType, adjustmentTypes } = useAdjustmentType();

  // Get scenario filter from context
  const { selectedScenario } = useScenario();

  const fileInputRef = useRef(null);

  // Get tag and subtag filters from context
  const { selectedTag } = useTagFilter();
  const { selectedSubtag } = useSubtagFilter();

  // Get entity filter from context
  const { selectedEntity } = useEntityFilter();

  // Get current user from auth
  const { user } = useAuth();

  // Function to format adjustment type names with spaces for display
  const formatAdjustmentType = (type) => {
    if (type === 'All') return 'All';
    if (!type) return '';

    // Special case handling for known types
    const typeMap = {
      'PretaxAdjustment': 'Pretax',
      'PayableAdjustment': 'Payable',
      'PermAdjustment': 'Permanent',
      'TempAdjustment': 'Temporary',
      'GSPermAdjustment': 'GS Permanent',
      'GSTempAdjustment': 'GS Temporary',
      'TIPermAdjustment': 'TI Permanent',
      'TITempAdjustment': 'TI Temporary',
      'CurrentProvisionBasisTaxAdjustment': 'Tax Adj. Provision',
      'CurrentReturnBasisTaxAdjustment': 'Tax Adj. Return',
      'ReturnToProvisionAdjustment': 'Tax Adj. RTP',
      'NetOperatingLossAdjustment': 'Net Operating Loss',
      'TaxEffectedTempAdjustment': 'Tax Effected Temporary',
      'ValuationAllowanceAdjustment': 'Valuation Allowance'
    };

    return typeMap[type] || type.replace(/([A-Z])/g, ' $1').trim();
  };

  // Store all fetched adjustments in one place for filtering
  const [allAdjustments, setAllAdjustments] = useState([]);

  // State adjustment types list
  const STATE_ADJUSTMENT_TYPES = [
  'State Pre-App Perm',
  'State Pre-App Temp',
  'State Post-App Perm',
  'State Post-App Temp',
  'State Tax Effected Temp',
  'State Tax Adj. Return',
  'State Tax Adj. Provision',
  'State Tax Adj. RTP',
  'State Pre-App NOL',
  'State Post-App NOL',
  'State Pre-App Valuation Allowance',
  'State Post-App Valuation Allowance',
  'State Tax Effected Valuation Allowance'];


  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAdjustment, setCurrentAdjustment] = useState({
    EntityCode: '',
    EntityName: '',
    SubEntity: '',
    AdjustmentCode: '',
    AdjustmentName: '',
    ActivityType: '',
    Amount: '',
    USDAmount: '',
    Tag: '',
    SubTag: '',
    AdjustmentType: '',
    ScenarioCode: selectedScenario?.scenarioCode || 'SC001',
    PointInTime: '',
    FilingCode: '',
    ModifiedBy: 'admin'
  });

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Point In Time management state
  const [pointInTimes, setPointInTimes] = useState([]);
  const [loadingPointInTimes, setLoadingPointInTimes] = useState(false);

  // State for row selection
  const [selectedRows, setSelectedRows] = useState([]);

  // State for FX rates - now stores all three types per entity
  const [entityFXRates, setEntityFXRates] = useState(new Map());


  // Get adjustment data based on selected type and scenario
  const fetchAdjustments = async () => {
    // Don't fetch if no scenario is selected
    if (!selectedScenario) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const scenarioCode = selectedScenario.scenarioCode;
      const allAdjustmentTypes = adjustmentTypes.filter((type) => type !== 'All');
      const allPromises = [];

      // Always fetch all adjustment types to populate the full dataset
      for (const type of allAdjustmentTypes) {
        const routePath = adjustmentTypeToRouteMap[type];
        if (routePath) {
          allPromises.push(
            api.get(`/adjustments/${routePath}`, {
              params: { scenarioCode }
            }).
            then((response) => {
              // Add adjustment type to each item for better identification
              if (Array.isArray(response?.data?.data)) {
                return response.data.data.map((item) => ({
                  ...item,
                  AdjustmentType: type, // Add type for filtering/display
                  id: item.id || `${type}-${Math.random().toString(36).substr(2, 9)}`
                }));
              }
              return [];
            }).
            catch((error) => {
              console.error(`Error fetching ${type} adjustments:`, error);
              return []; // Return empty array on error
            })
          );
        }
      }

      // Wait for all requests to complete
      const results = await Promise.allSettled(allPromises);

      // Combine all successful results and store them
      let fetchedAdjustments = results.
      filter((result) => result.status === 'fulfilled').
      flatMap((result) => result.value);

      // Fetch PointInTime names for display
      let pointInTimeMap = new Map();
      try {
        const pointInTimesResponse = await api.get('/point-in-times');
        (pointInTimesResponse.data.data || []).forEach((pit) => {
          pointInTimeMap.set(pit.Code, pit.Name);
        });
      } catch (error) {
        console.error('Error fetching point in times for display:', error);
      }

      // Populate AdjustmentName, EntityName, and PointInTime name using master lists
      fetchedAdjustments = fetchedAdjustments.map((adj) => {
        const adjustment = masterAdjustments.find((a) => a.AdjustmentCode === adj.AdjustmentCode);
        const entity = masterEntities.find((e) => e.EntityCode === adj.EntityCode);
        return {
          ...adj,
          AdjustmentName: adjustment ? adjustment.AdjustmentName : adj.AdjustmentName || '',
          EntityName: entity ? entity.EntityName : adj.EntityName || '',
          USDAmount: adj.USDAmount || '',
          PointInTime: pointInTimeMap.get(adj.PointInTime) || adj.PointInTime || ''
        };
      });
      // DEBUG: Log all adjustments and their ScenarioCode







      // Filter adjustments by selected scenario code (frontend safety)
      const scenarioFilteredAdjustments = fetchedAdjustments.filter(
        (adj) => adj.ScenarioCode === scenarioCode
      );
      // Store all fetched adjustments for filtering
      setAllAdjustments(scenarioFilteredAdjustments);

      // Now filter based on the selected adjustment type
      if (selectedAdjustmentType === 'All') {
        // For 'All' view, show all adjustments with an adjustment type column
        const displayType = 'Standard';
        const combinedColumns = getColumnsForAdjustmentType(displayType, handleEdit, handleDelete, masterEntities, masterAdjustments);

        // Add adjustment type column to the combined view
        const adjustmentTypeColumn = {
          field: 'AdjustmentType',
          headerName: 'Adjustment Type',
          minWidth: 140,
          renderCell: (params) =>
          <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
              {params.value}
            </Typography>

        };

        // Insert adjustment type column as the second column
        combinedColumns.splice(1, 0, adjustmentTypeColumn);
        setColumns(combinedColumns);
        setAdjustments(scenarioFilteredAdjustments);
      } else {
        // For specific type view, filter to show only that type
        const type = selectedAdjustmentType;
        const displayType = type;

        // Filter the adjustments for the selected type
        const filteredAdjustments = scenarioFilteredAdjustments.filter(
          (adj) => adj.AdjustmentType === selectedAdjustmentType
        );

        // Update columns based on adjustment type
        const adjustmentColumns = getColumnsForAdjustmentType(displayType, handleEdit, handleDelete, masterEntities, masterAdjustments);
        setColumns(adjustmentColumns);
        setAdjustments(filteredAdjustments);
      }
    } catch (error) {
      console.error(`Error fetching ${selectedAdjustmentType} adjustments:`, error);
      setNotification({
        open: true,
        message: `Failed to load ${selectedAdjustmentType} adjustments`,
        severity: 'error'
      });

      // Set default columns on error
      if (selectedAdjustmentType !== 'All') {
        setColumns(getColumnsForAdjustmentType('Standard', handleEdit, handleDelete, masterEntities, masterAdjustments));
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch master entities from API
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master entities:', error);
      setNotification({
        open: true,
        message: 'Failed to load master entities',
        severity: 'error'
      });
    } finally {
      setEntityLoading(false);
    }
  };

  // Fetch StateInputs filtered by EntityCode for the FilingCode dropdown
  const fetchStateInputsForFiling = async (entityCode, scenarioCode) => {
    if (!entityCode || !scenarioCode) {
      setStateInputsForFiling([]);
      return;
    }
    try {
      const response = await api.get('/states/input', {
        params: { scenarioCode, entityCode }
      });
      const data = Array.isArray(response.data) ? response.data : response.data?.data || [];
      const filtered = data.filter((si) => si.EntityCode === entityCode);
      setStateInputsForFiling(filtered);
    } catch (error) {
      console.error('Error fetching state inputs for filing code:', error);
      setStateInputsForFiling([]);
    }
  };

  // Fetch EntityInputs for the selected scenario
  const fetchEntityInputs = async () => {
    if (!selectedScenario) {
      setEntityInputs([]);
      return;
    }

    try {
      const response = await api.get('/entity-models/input', {
        params: { scenarioCode: selectedScenario.scenarioCode }
      });
      if (response.data) {
        setEntityInputs(response.data);

        // Build FX rates map for quick lookup - store all three rate types (use as-is, no conversion)
        const fxRatesMap = new Map();
        response.data.forEach((entityInput) => {
          if (entityInput.EntityCode) {
            const rates = {};

            // Parse and store Average FX Rate (use as-is, no percentage conversion)
            if (entityInput.AverageFXRate) {
              rates.average = parseFloat(entityInput.AverageFXRate);
            }

            // Parse and store Beginning Spot Rate (use as-is, no percentage conversion)
            if (entityInput.BeginningSpotRate) {
              rates.beginning = parseFloat(entityInput.BeginningSpotRate);
            }

            // Parse and store Ending Spot Rate (use as-is, no percentage conversion)
            if (entityInput.EndingSpotRate) {
              rates.ending = parseFloat(entityInput.EndingSpotRate);
            }

            fxRatesMap.set(entityInput.EntityCode, rates);
          }
        });
        setEntityFXRates(fxRatesMap);
      }
    } catch (error) {
      console.error('Error fetching entity inputs:', error);
      setEntityInputs([]);
      setEntityFXRates(new Map());
    }
  };

  // Look up entity name based on entity code
  const lookupEntityName = (entityCode) => {
    if (!entityCode) {
      setEntityName('');
      setCurrentAdjustment((prev) => ({ ...prev, EntityName: '' }));
      return;
    }
    // Use filtered entities instead of all master entities
    const entity = filteredEntities.find((e) => e.EntityCode === entityCode);
    if (entity) {
      setEntityName(entity.EntityName || '');
      setCurrentAdjustment((prev) => ({
        ...prev,
        EntityName: entity.EntityName || ''
      }));

      // Trigger currency conversion if amounts exist (but not for pretax adjustments)
      const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
      if (!isPretax) {
        const currentAmount = currentAdjustment.Amount;
        const currentUSDAmount = currentAdjustment.USDAmount;
        if (currentAmount && !currentUSDAmount) {
          // If local amount exists but USD doesn't, convert local to USD
          handleAmountChange(currentAmount);
        } else if (currentUSDAmount && !currentAmount) {
          // If USD amount exists but local doesn't, convert USD to local
          handleUSDAmountChange(currentUSDAmount);
        }
      }
    } else {
      setEntityName('');
      setCurrentAdjustment((prev) => ({ ...prev, EntityName: '' }));
    }
  };

  // Look up entity code based on entity name
  const lookupEntityCode = (entityNameInput) => {
    if (!entityNameInput) {
      setCurrentAdjustment((prev) => ({ ...prev, EntityCode: '' }));
      return;
    }
    // Use filtered entities instead of all master entities
    const entity = filteredEntities.find((e) => e.EntityName.toLowerCase() === entityNameInput.toLowerCase());
    if (entity) {
      setCurrentAdjustment((prev) => ({
        ...prev,
        EntityCode: entity.EntityCode
      }));

      // Trigger currency conversion if amounts exist (but not for pretax adjustments)
      const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
      if (!isPretax) {
        const currentAmount = currentAdjustment.Amount;
        const currentUSDAmount = currentAdjustment.USDAmount;
        if (currentAmount && !currentUSDAmount) {
          // If local amount exists but USD doesn't, convert local to USD
          setTimeout(() => handleAmountChange(currentAmount), 0);
        } else if (currentUSDAmount && !currentAmount) {
          // If USD amount exists but local doesn't, convert USD to local
          setTimeout(() => handleUSDAmountChange(currentUSDAmount), 0);
        }
      }
    }
  };


  // Look up adjustment name based on adjustment code
  const lookupAdjustmentName = (adjustmentCode) => {
    if (!adjustmentCode) {
      setAdjustmentName('');
      setCurrentAdjustment((prev) => ({ ...prev, AdjustmentName: '' }));
      return;
    }
    const adj = masterAdjustments.find((a) => a.AdjustmentCode === adjustmentCode);
    if (adj) {
      setAdjustmentName(adj.AdjustmentName || '');
      setCurrentAdjustment((prev) => ({ ...prev, AdjustmentName: adj.AdjustmentName || '' }));
    } else {
      setAdjustmentName('');
      setCurrentAdjustment((prev) => ({ ...prev, AdjustmentName: '' }));
    }
  };

  // Look up adjustment code based on adjustment name
  const lookupAdjustmentCode = (adjustmentNameInput) => {
    if (!adjustmentNameInput) {
      setCurrentAdjustment((prev) => ({ ...prev, AdjustmentCode: '' }));
      return;
    }
    const adj = masterAdjustments.find((a) => a.AdjustmentName && a.AdjustmentName.toLowerCase() === adjustmentNameInput.toLowerCase());
    if (adj) {
      setCurrentAdjustment((prev) => ({ ...prev, AdjustmentCode: adj.AdjustmentCode }));
    }
  };

  // Get FX rate for an entity based on activity type
  const getFXRateForEntity = (entityCode, activityType) => {
    if (!entityCode) return null;

    const rates = entityFXRates.get(entityCode);
    if (!rates) return null;

    // Determine which rate to use based on activity type
    // Beginning Balance and Return to Provision use Beginning Spot Rate
    if (activityType === 'BeginningBalance' || activityType === 'Returntoprovision') {
      return rates.beginning || null;
    }

    // Current Activity PL and EQ use Average FX Rate
    if (activityType === 'CurrentActivityPL' || activityType === 'CurrentActivityEQ') {
      return rates.average || null;
    }

    // All other activity types use Ending Spot Rate
    // DeferredTrueUp, PurchaseAccounting, OCIAdjustment, DeferredExpAdjustment,
    // BalanceSheetAdjustment, CustomDeferredExpAdjustment, CustomBalanceSheetAdjustment
    return rates.ending || null;
  };

  // Recalculate amounts when entity or activity type changes
  // Local currency is priority; if both filled keep local and recalculate USD;
  // if only USD is filled, keep USD and recalculate local.
  const recalculateAmounts = (newEntityCode, newActivityType, currentAmount, currentUSDAmount, adjustmentType) => {
    const isPretax = adjustmentType === 'Pretax' || adjustmentType === 'PretaxAdjustment';
    if (isPretax) return {};

    const fxRate = getFXRateForEntity(newEntityCode, newActivityType);
    if (!fxRate) return {};

    const hasLocal = currentAmount !== '' && currentAmount != null;
    const hasUSD = currentUSDAmount !== '' && currentUSDAmount != null;

    if (hasLocal) {
      // Local is priority — recalculate USD
      return { USDAmount: (parseFloat(currentAmount) / fxRate).toFixed(2) };
    } else if (hasUSD) {
      // Only USD filled — recalculate local
      return { Amount: (parseFloat(currentUSDAmount) * fxRate).toFixed(2) };
    }
    return {};
  };

  // Handle local currency amount change with automatic USD conversion
  const handleAmountChange = (value) => {
    // Remove commas for calculation
    const numericValue = removeCommas(value);
    const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
    let usdAmount = '';

    // Skip automatic conversion for pretax adjustments
    const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';

    if (numericValue && fxRate && !isPretax) {
      // USD = Local / FX Rate
      usdAmount = (parseFloat(numericValue) / fxRate).toFixed(2);
    }

    setCurrentAdjustment({
      ...currentAdjustment,
      Amount: numericValue,
      USDAmount: isPretax ? currentAdjustment.USDAmount : usdAmount
    });
  };

  // Handle USD amount change with automatic local currency conversion
  const handleUSDAmountChange = (value) => {
    // Remove commas for calculation
    const numericValue = removeCommas(value);
    const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
    let localAmount = '';

    // Skip automatic conversion for pretax adjustments
    const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';

    if (numericValue && fxRate && !isPretax) {
      // Local = USD * FX Rate
      localAmount = (parseFloat(numericValue) * fxRate).toFixed(2);
    }

    setCurrentAdjustment({
      ...currentAdjustment,
      Amount: isPretax ? currentAdjustment.Amount : localAmount,
      USDAmount: numericValue
    });
  };

  // Fetch Point In Times for the dropdown
  const fetchPointInTimes = async () => {
    setLoadingPointInTimes(true);
    try {
      const response = await api.get('/point-in-times');
      setPointInTimes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching point in times:', error);
    } finally {
      setLoadingPointInTimes(false);
    }
  };

  // Fetch master adjustments from API
  const fetchMasterAdjustments = async () => {
    setAdjustmentLoading(true);
    try {
      const response = await api.get('/adjustments/master');
      if (response.data && response.data.data) {
        setMasterAdjustments(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching master adjustments:', error);
      setNotification({
        open: true,
        message: 'Failed to load master adjustments',
        severity: 'error'
      });
    } finally {
      setAdjustmentLoading(false);
    }
  };


  // Fetch dependencies when component mounts
  useEffect(() => {
    fetchMasterEntities();
    fetchMasterAdjustments();
  }, []);

  // Fetch EntityInputs when scenario changes
  useEffect(() => {
    if (selectedScenario) {
      fetchEntityInputs();
    }
  }, [selectedScenario]);

  // Filter entities by scenario's PointInTime AND entityList
  useEffect(() => {
    if (!selectedScenario || !masterEntities.length) {
      setFilteredEntities([]);
      return;
    }

    // Get scenario's PointInTime and entityList
    const scenarioPointInTime = selectedScenario.PointInTime;
    const scenarioEntityCodes = selectedScenario.entityList || [];

    // Filter entities that match BOTH the scenario's PointInTime AND are in the entityList
    let filtered = masterEntities;

    // First filter by PointInTime (required)
    if (scenarioPointInTime) {
      filtered = filtered.filter((entity) => entity.PointInTime === scenarioPointInTime);
    }

    // Then filter by entityList if it exists
    if (scenarioEntityCodes.length > 0) {
      filtered = filtered.filter((entity) =>
      scenarioEntityCodes.includes(entity.EntityCode)
      );
    } else if (entityInputs.length > 0) {
      // Fallback: Get unique entity codes from EntityInputs for the current scenario
      const entityCodesWithInputs = [...new Set(entityInputs.map((input) => input.EntityCode))];
      filtered = filtered.filter((entity) =>
      entityCodesWithInputs.includes(entity.EntityCode)
      );
    }

    setFilteredEntities(filtered);
  }, [masterEntities, selectedScenario, entityInputs]);

  // Refetch adjustments when selected scenario changes
  useEffect(() => {
    if (selectedScenario) {
      // Set ScenarioCode and PointInTime in current adjustment template
      // Use the PointInTime field from the scenario (this should be the code)
      const pointInTimeCode = selectedScenario.PointInTime || '';

      setCurrentAdjustment((prev) => ({
        ...prev,
        ScenarioCode: selectedScenario.scenarioCode,
        PointInTime: pointInTimeCode
      }));

      // Fetch new data when scenario changes
      fetchAdjustments();
    }
    // We avoid adding fetchAdjustments as a dependency to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScenario]);

  // This effect runs once when the component mounts to fetch data
  useEffect(() => {
    if (selectedAdjustmentType && selectedScenario) {
      // Only fetch once on initial load
      fetchAdjustments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // This effect handles filtering when adjustment type changes
  // But does NOT trigger new API calls
  useEffect(() => {
    if (selectedAdjustmentType && allAdjustments.length > 0) {
      // Filter the existing data without making API calls
      if (selectedAdjustmentType === 'All') {
        // Show all adjustments for 'All'
        const displayType = 'Standard';
        const combinedColumns = getColumnsForAdjustmentType(displayType, handleEdit, handleDelete, masterEntities, masterAdjustments);

        // Add adjustment type column to the combined view
        const adjustmentTypeColumn = {
          field: 'AdjustmentType',
          headerName: 'Adjustment Type',
          flex: 1,
          minWidth: 180,
          renderCell: (params) =>
          <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
              {params.value}
            </Typography>

        };

        // Insert adjustment type column as the second column
        combinedColumns.splice(1, 0, adjustmentTypeColumn);
        setColumns(combinedColumns);
        setAdjustments(allAdjustments);
      } else {
        // Filter for specific adjustment type
        const filteredAdjustments = allAdjustments.filter(
          (adj) => adj.AdjustmentType === selectedAdjustmentType
        );
        const displayType = selectedAdjustmentType;
        // Update columns based on adjustment type
        const adjustmentColumns = getColumnsForAdjustmentType(displayType, handleEdit, handleDelete, masterEntities, masterAdjustments);
        setColumns(adjustmentColumns);
        setAdjustments(filteredAdjustments);
      }
    }
    // We intentionally don't include allAdjustments as a dependency
    // to avoid potential infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdjustmentType]);

  // Apply tag, subtag, and entity filtering to displayed adjustments
  useEffect(() => {
    if (!allAdjustments.length) return;

    // Start with adjustments filtered by adjustment type
    let filteredAdjustments = allAdjustments;

    // Apply adjustment type filter
    if (selectedAdjustmentType && selectedAdjustmentType !== 'All') {
      filteredAdjustments = filteredAdjustments.filter(
        (adj) => adj.AdjustmentType === selectedAdjustmentType
      );
    }

    // Apply entity filter
    const entityArr = Array.isArray(selectedEntity) ? selectedEntity : [];
    const plainEntities = entityArr.filter((e) => !e.startsWith('[Group] '));
    if (plainEntities.length > 0) {
      filteredAdjustments = filteredAdjustments.filter((adj) => plainEntities.includes(adj.EntityCode));
    }

    // Apply tag filter
    if (selectedTag && selectedTag !== 'All') {
      filteredAdjustments = filteredAdjustments.filter(
        (adj) => adj.Tag === selectedTag
      );
    }

    // Apply subtag filter
    if (selectedSubtag && selectedSubtag !== 'All') {
      filteredAdjustments = filteredAdjustments.filter(
        (adj) => adj.SubTag === selectedSubtag
      );
    }

    setAdjustments(filteredAdjustments);

    // Note: Column updates will be handled when functions are available
  }, [selectedAdjustmentType, selectedEntity, selectedTag, selectedSubtag, allAdjustments]);

  // Handle adding a new adjustment
  const handleAdd = () => {
    setEditMode(false);

    // Get PointInTime code from selected scenario
    const pointInTimeCode = selectedScenario?.PointInTime || '';




    setCurrentAdjustment({
      EntityCode: '',
      EntityName: '',
      SubEntity: '',
      AdjustmentCode: '',
      AdjustmentName: '',
      ActivityType: '',
      Amount: '',
      USDAmount: '',
      Tag: '',
      SubTag: '',
      AdjustmentType: '',
      ScenarioCode: selectedScenario?.scenarioCode || 'SC001',
      PointInTime: pointInTimeCode,
      FilingCode: '',
      ModifiedBy: 'admin'
    });
    setEntityName('');
    setAdjustmentName('');
    setStateInputsForFiling([]);
    fetchPointInTimes();
    setOpenDialog(true);
  };

  // Handle editing an adjustment
  const handleEdit = (adjustment) => {
    setEditMode(true);
    const amount = adjustment.Amount || adjustment.Value || '';

    setCurrentAdjustment({
      id: adjustment.id,
      AdjustmentId: adjustment.AdjustmentId,
      EntityCode: adjustment.EntityCode || '',
      EntityName: adjustment.EntityName || '',
      SubEntity: adjustment.SubEntity || '',
      AdjustmentCode: adjustment.AdjustmentCode || '',
      AdjustmentName: adjustment.AdjustmentName || '',
      ActivityType: adjustment.ActivityType || '',
      Amount: amount,
      USDAmount: adjustment.USDAmount || '',
      Tag: adjustment.Tag || '',
      SubTag: adjustment.SubTag || '',
      AdjustmentType: adjustment.AdjustmentType || '',
      ScenarioCode: adjustment.ScenarioCode || selectedScenario?.scenarioCode || 'SC001',
      PointInTime: adjustment.PointInTime || '',
      FilingCode: adjustment.FilingCode || '',
      ModifiedBy: 'admin'
    });
    setEntityName(adjustment.EntityName || '');
    setAdjustmentName(adjustment.AdjustmentName || '');
    // Pre-fetch state inputs if editing a state adjustment type
    const adjType = adjustment.AdjustmentType || '';
    const stateTypes = [
    'State Pre-App Perm', 'State Pre-App Temp', 'State Post-App Perm', 'State Post-App Temp',
    'State Tax Effected Temp', 'State Tax Adj. Return', 'State Tax Adj. Provision',
    'State Tax Adj. RTP', 'State Pre-App NOL', 'State Post-App NOL',
    'State Pre-App Valuation Allowance', 'State Post-App Valuation Allowance',
    'State Tax Effected Valuation Allowance'];

    if (stateTypes.includes(adjType) && adjustment.EntityCode) {
      fetchStateInputsForFiling(
        adjustment.EntityCode,
        adjustment.ScenarioCode || selectedScenario?.scenarioCode
      );
    } else {
      setStateInputsForFiling([]);
    }
    fetchPointInTimes();
    setOpenDialog(true);
  };

  // Handle selection change
  const handleSelectionChange = (newSelectionModel) => {
    setSelectedRows(newSelectionModel);
  };

  // Handle deleting selected adjustments
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    const rowsToDelete = [...selectedRows];
    if (window.confirm(`Are you sure you want to delete ${rowsToDelete.length} selected adjustments?`)) {
      setDeleting(true);
      try {
        // Delete each selected adjustment from backend
        for (const id of rowsToDelete) {
          const adj = adjustments.find((a) => a.id === id);
          if (!adj) continue;
          const routePath = adjustmentTypeToRouteMap[adj.AdjustmentType];
          if (!routePath) continue;
          await api.delete(`/adjustments/${routePath}/${id}`);
        }
        setSelectedRows([]);
        setNotification({
          open: true,
          message: `Successfully deleted ${rowsToDelete.length} adjustments`,
          severity: 'success'
        });
        fetchAdjustments();
      } catch (error) {
        setNotification({
          open: true,
          message: 'Failed to delete some adjustments',
          severity: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };


  // Handle deleting a single adjustment
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this adjustment?')) {
      try {

        const adj = adjustments.find((a) => a.id === id);

        if (!adj) {

          return;
        }
        const routePath = adjustmentTypeToRouteMap[adj.AdjustmentType];

        if (!routePath) throw new Error('No route configured for this adjustment type');


        const response = await api.delete(`/adjustments/${routePath}/${id}`);


        setAdjustments(adjustments.filter((adj) => adj.id !== id));
        setNotification({
          open: true,
          message: 'Adjustment deleted successfully',
          severity: 'success'
        });
        fetchAdjustments();
      } catch (error) {
        console.error('DEBUG: Delete error:', error);
        console.error('DEBUG: Error response:', error.response);
        setNotification({
          open: true,
          message: `Failed to delete adjustment: ${error.response?.data?.message || error.message}`,
          severity: 'error'
        });
      }
    }
  };

  // Handle import click
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Handle file selection for import
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Get first worksheet
          const worksheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[worksheetName];

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            throw new Error('The Excel file is empty');
          }

          // Get all column names from the first row, case-insensitive
          const fileColumns = Object.keys(jsonData[0] || {});
          const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

          // Fetch PointInTime master data to map names to codes
          const pointInTimesResponse = await api.get('/point-in-times');
          const pointInTimes = pointInTimesResponse.data.data || [];

          // Create a map from PointInTime Name to Code (case-insensitive)
          const pitNameToCodeMap = new Map();
          pointInTimes.forEach((pit) => {
            pitNameToCodeMap.set(pit.Name.toLowerCase().trim(), pit.Code);
            // Also map the code to itself in case Excel has codes
            pitNameToCodeMap.set(pit.Code.toLowerCase().trim(), pit.Code);
          });

          // Map frontend column names to possible Excel column names
          const columnMappings = {
            'AdjustmentCode': ['adjustment code', 'adjustmentcode', 'code'],
            'AdjustmentType': ['adjustment type', 'adjustmenttype', 'type'],
            'EntityCode': ['entity code', 'entitycode', 'filing entity code', 'filing entity'],
            'EntityName': ['entity name', 'entityname', 'name'],
            'SubEntity': ['sub entity', 'subentity', 'sub-entity'],
            'ActivityType': ['activity type', 'activitytype'],
            'Amount': ['amount'],
            'USDAmount': ['usd amount', 'usdamount', 'usd_amount'],
            'Tag': ['tag'],
            'SubTag': ['sub tag', 'subtag', 'sub-tag'],
            'PointInTime': ['point in time', 'pointintime', 'pit', 'period'],
            'FilingCode': ['filing code', 'filingcode', 'filing']
          };

          // Find matching columns in the Excel file
          const matchedColumns = {};
          let missingColumns = [];

          for (const [field, possibleNames] of Object.entries(columnMappings)) {
            const found = fileColumnsLower.some((col, index) => {
              if (possibleNames.includes(col.toLowerCase())) {
                matchedColumns[field] = fileColumns[index]; // Store original column name
                return true;
              }
              return false;
            });

            // Only these fields are required
            if (!found && ['AdjustmentCode', 'AdjustmentType', 'EntityCode', 'ActivityType', 'Amount'].includes(field)) {
              missingColumns.push(possibleNames[0]); // Use the first name as the display name
            }
          }

          if (missingColumns.length > 0) {
            throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
          }

          // Fetch existing adjustments, master adjustments, and entity inputs for FX rates
          // Need to fetch all adjustment types for the current scenario to check for duplicates
          const allAdjustmentTypesFiltered = adjustmentTypes.filter((type) => type !== 'All');
          const existingAdjustmentsPromises = allAdjustmentTypesFiltered.map((type) => {
            const routePath = adjustmentTypeToRouteMap[type];
            if (!routePath) return Promise.resolve([]);
            return api.get(`/adjustments/${routePath}`, {
              params: { scenarioCode: selectedScenario?.scenarioCode }
            }).
            then((response) => {
              if (Array.isArray(response?.data?.data)) {
                return response.data.data.map((item) => ({
                  ...item,
                  AdjustmentType: type
                }));
              }
              return [];
            }).
            catch((error) => {
              console.error(`Error fetching ${type} adjustments for import:`, error);
              return [];
            });
          });

          const [masterAdjustmentsResponse, entityInputsResponse, ...existingAdjustmentsResults] = await Promise.all([
          api.get('/adjustments/master'), // Get master adjustment definitions
          api.get('/entity-models/input', { params: { scenarioCode: selectedScenario?.scenarioCode } }),
          ...existingAdjustmentsPromises]
          );

          // Flatten all existing adjustments from all types
          const existingAdjustments = existingAdjustmentsResults.flat();

          // Create a map using ALL identifying fields (excluding Amount and USDAmount which are editable)
          const adjustmentsMap = new Map(
            existingAdjustments.map((adj) => {
              const key = [
              adj.ScenarioCode,
              adj.EntityCode,
              adj.SubEntity || '',
              adj.AdjustmentCode,
              adj.ActivityType,
              adj.Tag || '',
              adj.SubTag || '',
              adj.PointInTime,
              adj.AdjustmentType].
              join('|').toLowerCase();
              return [key, adj];
            })
          );

          // Create a map of adjustment codes to their names from master data
          const masterAdjustmentMap = new Map();
          (masterAdjustmentsResponse.data.data || []).forEach((adj) => {
            if (adj.AdjustmentCode && adj.AdjustmentName) {
              masterAdjustmentMap.set(adj.AdjustmentCode.toLowerCase(), adj.AdjustmentName);
            }
          });

          // Build FX rates map from entity inputs - use rates directly as stored (no conversion)
          const fxRatesMap = new Map();
          (entityInputsResponse.data || []).forEach((entityInput) => {
            if (entityInput.EntityCode) {
              const rates = {};

              // Parse and store Average FX Rate (use as-is, no percentage conversion)
              if (entityInput.AverageFXRate) {
                rates.average = parseFloat(entityInput.AverageFXRate);
              }

              // Parse and store Beginning Spot Rate (use as-is, no percentage conversion)
              if (entityInput.BeginningSpotRate) {
                rates.beginning = parseFloat(entityInput.BeginningSpotRate);
              }

              // Parse and store Ending Spot Rate (use as-is, no percentage conversion)
              if (entityInput.EndingSpotRate) {
                rates.ending = parseFloat(entityInput.EndingSpotRate);
              }

              fxRatesMap.set(entityInput.EntityCode, rates);
            }
          });

          // Process each row and track results
          const importResults = {
            created: 0,
            updated: 0,
            errors: [],
            duplicatesInFile: 0
          };

          // Track rows processed in this import to detect duplicates within the file
          const processedInThisImport = new Set();

          // Process rows sequentially to avoid race conditions
          for (let i = 0; i < jsonData.length; i++) {
            try {
              const row = jsonData[i];

              // Extract values using matched column names
              const adjustmentCode = String(row[matchedColumns.AdjustmentCode] || '').trim();
              const adjustmentType = String(row[matchedColumns.AdjustmentType] || '').trim();
              const entityCode = String(row[matchedColumns.EntityCode] || '').trim();
              const entityName = String(row[matchedColumns.EntityName] || '').trim();
              const subEntity = String(row[matchedColumns.SubEntity] || '').trim();
              const activityType = String(row[matchedColumns.ActivityType] || '').trim();
              const amount = row[matchedColumns.Amount];
              const usdAmount = row[matchedColumns.USDAmount];
              const tag = String(row[matchedColumns.Tag] || '').trim();
              const subTag = String(row[matchedColumns.SubTag] || '').trim();
              const filingCode = String(row[matchedColumns.FilingCode] || '').trim();

              // Handle PointInTime: use from file if present, otherwise use selected PointInTime
              let pointInTimeCode = '';
              if (matchedColumns.PointInTime) {
                const pointInTimeInput = String(row[matchedColumns.PointInTime] || '').trim();
                if (pointInTimeInput) {
                  pointInTimeCode = pitNameToCodeMap.get(pointInTimeInput.toLowerCase().trim());
                  if (!pointInTimeCode) {
                    throw new Error(`Invalid Point In Time: "${pointInTimeInput}". Must match a valid PointInTime Name or Code.`);
                  }
                }
              }

              // If no PointInTime from file, use scenario's PointInTime
              if (!pointInTimeCode && selectedScenario?.PointInTime) {
                pointInTimeCode = selectedScenario.PointInTime;
              }

              // If still no PointInTime, use first available
              if (!pointInTimeCode && pointInTimes.length > 0) {
                pointInTimeCode = pointInTimes[0].Code;
              }

              if (!pointInTimeCode) {
                throw new Error('Point In Time is required but not found in file or context');
              }

              // Validate required fields
              if (!adjustmentCode) {
                throw new Error('Adjustment Code is required');
              }
              if (!adjustmentType) {
                throw new Error('Adjustment Type is required');
              }

              // Validate adjustment type against predefined types
              const validAdjustmentTypes = adjustmentTypes.filter((type) => type !== 'All');
              if (!validAdjustmentTypes.includes(adjustmentType)) {
                throw new Error(`Invalid Adjustment Type: "${adjustmentType}". Must be one of: ${validAdjustmentTypes.join(', ')}`);
              }

              if (!entityCode) {
                throw new Error('Entity Code is required');
              }

              // Validate EntityCode exists in filtered entities (must be associated with this scenario)
              const validEntity = filteredEntities.find((e) => e.EntityCode === entityCode);
              if (!validEntity) {
                throw new Error(`Entity Code "${entityCode}" is not associated with the selected scenario "${selectedScenario?.scenarioCode}".`);
              }

              // Validate SubEntity if provided
              if (subEntity) {
                const validSubEntities = ['Base', 'topside', 'topside us', 'branch'];
                if (!validSubEntities.includes(subEntity)) {
                  throw new Error(`Invalid SubEntity: "${subEntity}". Must be one of: ${validSubEntities.join(', ')}`);
                }
              }

              if (!activityType) {
                throw new Error('Activity Type is required');
              }

              // Validate ActivityType is valid for this AdjustmentType
              const validActivityTypes = getActivityTypeOptions(adjustmentType);
              if (validActivityTypes.length > 0 && !validActivityTypes.includes(activityType)) {
                throw new Error(`Invalid Activity Type "${activityType}" for Adjustment Type "${adjustmentType}". Valid options are: ${validActivityTypes.join(', ')}`);
              }

              // Validate Tag if provided
              if (tag && !TAG_OPTIONS.includes(tag)) {
                throw new Error(`Invalid Tag: "${tag}". Must be one of: ${TAG_OPTIONS.join(', ')}`);
              }

              // Validate SubTag if provided
              if (subTag && !SUBTAG_OPTIONS.includes(subTag)) {
                throw new Error(`Invalid SubTag: "${subTag}". Must be one of: ${SUBTAG_OPTIONS.join(', ')}`);
              }

              if (amount === undefined || amount === null || amount === '') {
                throw new Error('Amount is required');
              }

              // Parse numeric values
              let parsedAmount = parseFloat(removeCommas(String(amount)));
              // Amount (local currency) is the source of truth during import.
              // USDAmount, if needed, will be derived from Amount using the correct FX rate.
              let parsedUSDAmount = null;

              // For Pretax, allow user to import both Amount and USDAmount without overriding.
              const isPretax = adjustmentType.toLowerCase().includes('pretax');
              if (isPretax && usdAmount != null && usdAmount !== '') {
                parsedUSDAmount = parseFloat(removeCommas(String(usdAmount)));
                if (isNaN(parsedUSDAmount)) {
                  throw new Error(`Invalid USD Amount value: "${usdAmount}"`);
                }
              }

              if (isNaN(parsedAmount)) {
                throw new Error(`Invalid Amount value: "${amount}"`);
              }

              // Get FX rate for this entity based on activity type (matching getFXRateForEntity logic)
              const entityRates = fxRatesMap.get(entityCode);
              let fxRate = null;
              if (entityRates) {
                // Beginning Balance and Return to Provision use Beginning Spot Rate
                if (activityType === 'BeginningBalance' || activityType === 'ReturnToProvision') {
                  fxRate = entityRates.beginning || null;
                }
                // Current Activity PL and EQ use Average FX Rate
                else if (activityType === 'CurrentActivityPL' || activityType === 'CurrentActivityEQ') {
                  fxRate = entityRates.average || null;
                }
                // All other activity types use Ending Spot Rate
                else {
                  fxRate = entityRates.ending || null;
                }
              }

              // Apply FX conversion logic for import:
              // Always derive USD from local currency Amount using the activity-specific FX rate.
              if (!isPretax && fxRate) {
                parsedUSDAmount = parseFloat((parsedAmount / fxRate).toFixed(2));
              }

              // Look up AdjustmentName from master data if not in file
              const adjustmentName = masterAdjustmentMap.get(adjustmentCode.toLowerCase()) || '';

              if (!adjustmentName) {
                throw new Error(`Adjustment Name not found for code "${adjustmentCode}". Please ensure the adjustment code exists in the master adjustments.`);
              }

              // Get the proper route for the adjustment type
              const routePath = adjustmentTypeToRouteMap[adjustmentType];
              if (!routePath) {
                throw new Error(`No route configured for adjustment type: ${adjustmentType}`);
              }

              const adjustmentData = {
                AdjustmentCode: adjustmentCode,
                AdjustmentName: adjustmentName,
                AdjustmentType: adjustmentType,
                EntityCode: entityCode,
                EntityName: entityName,
                SubEntity: subEntity,
                ActivityType: activityType,
                Amount: parsedAmount,
                USDAmount: parsedUSDAmount,
                Tag: tag,
                SubTag: subTag,
                PointInTime: pointInTimeCode,
                ScenarioCode: selectedScenario?.scenarioCode,
                FilingCode: filingCode || null,
                modifiedBy: user?.email || user?.sub || 'admin'
              };

              // Check if adjustment with this combination already exists
              // Use all identifying fields (excluding Amount and USDAmount which are editable)
              const adjustmentKey = [
              selectedScenario?.scenarioCode,
              entityCode,
              subEntity,
              adjustmentCode,
              activityType,
              tag,
              subTag,
              pointInTimeCode,
              adjustmentType,
              filingCode].
              join('|').toLowerCase();

              // Check if this exact row was already processed in this import file
              if (processedInThisImport.has(adjustmentKey)) {
                // Duplicate within the import file - update the amounts of the existing record
                const existingAdjustment = adjustmentsMap.get(adjustmentKey);
                if (existingAdjustment) {
                  await api.put(`/adjustments/${routePath}/${existingAdjustment.id}`, adjustmentData);
                  importResults.updated++;
                  importResults.duplicatesInFile++;
                }
                continue; // Skip to next row
              }

              const existingAdjustment = adjustmentsMap.get(adjustmentKey);

              if (existingAdjustment) {
                // Update existing adjustment using type-specific endpoint
                await api.put(`/adjustments/${routePath}/${existingAdjustment.id}`, adjustmentData);
                importResults.updated++;
              } else {
                // Create new adjustment using type-specific endpoint
                const response = await api.post(`/adjustments/${routePath}`, adjustmentData);
                importResults.created++;

                // Add to adjustmentsMap to prevent duplicates within this import
                const newAdjustment = response.data.data || response.data;
                adjustmentsMap.set(adjustmentKey, newAdjustment);
              }

              // Mark this combination as processed in this import
              processedInThisImport.add(adjustmentKey);

            } catch (error) {
              console.error(`Error processing row ${i + 1}:`, jsonData[i], error);
              console.error(`Full error response:`, error.response?.data);
              const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error';
              importResults.errors.push(`Row ${i + 1}: ${errorMessage}`);
            }
          }

          // Check if there were any errors
          if (importResults.errors.length > 0) {
            const errorMessage = importResults.errors.length === jsonData.length ?
            'All rows had errors. No data was imported.' :
            `Completed with ${importResults.errors.length} error(s) out of ${jsonData.length} rows.`;

            if (importResults.created === 0 && importResults.updated === 0) {
              throw new Error(`Import failed: ${errorMessage}\n\n${importResults.errors.join('\n')}`);
            } else {
              // Some rows succeeded, show warning
              const duplicateMsg = importResults.duplicatesInFile > 0 ? `, ${importResults.duplicatesInFile} duplicate(s) in file` : '';
              setNotification({
                open: true,
                message: `Partial import: ${importResults.created} created, ${importResults.updated} updated, ${importResults.errors.length} errors${duplicateMsg}`,
                severity: 'warning'
              });
            }
          }

          // Refresh data
          await fetchAdjustments();

          // Only show success message if no errors were shown before
          if (importResults.errors.length === 0) {
            const updatedMsg = importResults.updated > 0 ? `, updated ${importResults.updated}` : '';
            const duplicateMsg = importResults.duplicatesInFile > 0 ? `, ${importResults.duplicatesInFile} duplicate(s) in file handled` : '';
            setNotification({
              open: true,
              message: `Successfully imported ${importResults.created} adjustment${importResults.created !== 1 ? 's' : ''}${updatedMsg}${duplicateMsg}`,
              severity: 'success'
            });
          }

        } catch (error) {
          setNotification({
            open: true,
            message: error.message,
            severity: 'error'
          });
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      setNotification({
        open: true,
        message: error.message,
        severity: 'error'
      });
    }
    setImporting(false);
    event.target.value = null;
  };

  // Columns are dynamically loaded based on adjustment type in fetchAdjustments

  // Update columns when adjustment type changes (after functions are defined)
  useEffect(() => {
    if (!adjustments.length) return;

    // Update columns based on current filters
    if (selectedAdjustmentType === 'All') {
      // Show all adjustments with adjustment type column
      const displayType = 'Standard';
      const combinedColumns = getColumnsForAdjustmentType(displayType, handleEdit, handleDelete, masterEntities, masterAdjustments);

      // Add adjustment type column to the combined view
      const adjustmentTypeColumn = {
        field: 'AdjustmentType',
        headerName: 'Adjustment Type',
        flex: 1,
        minWidth: 180,
        renderCell: (params) =>
        <Typography variant="body2" sx={{ textAlign: 'left', width: '100%' }}>
            {params.value}
          </Typography>

      };

      // Insert adjustment type column as the second column
      combinedColumns.splice(1, 0, adjustmentTypeColumn);
      setColumns(combinedColumns);
    } else {
      // Update columns for specific adjustment type
      const displayType = selectedAdjustmentType;
      const adjustmentColumns = getColumnsForAdjustmentType(displayType, handleEdit, handleDelete, masterEntities, masterAdjustments);
      setColumns(adjustmentColumns);
    }
  }, [selectedAdjustmentType, adjustments, masterEntities, masterAdjustments]);

  // Fetch state inputs when entity or scenario changes while dialog is open for state adj types
  useEffect(() => {
    if (!openDialog) return;
    const isStateType = STATE_ADJUSTMENT_TYPES.includes(currentAdjustment.AdjustmentType);
    if (isStateType && currentAdjustment.EntityCode && selectedScenario?.scenarioCode) {
      fetchStateInputsForFiling(currentAdjustment.EntityCode, selectedScenario.scenarioCode);
    } else if (!isStateType) {
      setStateInputsForFiling([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDialog, currentAdjustment.EntityCode, currentAdjustment.AdjustmentType]);

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  // Handle closing the dialog
  const handleClose = () => {
    setOpenDialog(false);
    setStateInputsForFiling([]);
    setConfirmCloseOpen(false);
  };

  const handleRequestClose = () => {
    setConfirmCloseOpen(true);
  };

  // Handle saving an adjustment
  const handleSave = async () => {

    try {
      // Validate required fields
      if (!currentAdjustment.EntityCode) {
        throw new Error('Entity Code is required');
      }
      if (!currentAdjustment.AdjustmentCode) {
        throw new Error('Adjustment Code is required');
      }
      if (!currentAdjustment.AdjustmentType) {
        throw new Error('Adjustment Type is required');
      }
      if (!currentAdjustment.Amount) {
        throw new Error('Amount is required');
      }
      if (!currentAdjustment.ActivityType) {
        throw new Error('Activity Type is required');
      }
      if (!currentAdjustment.Tag) {
        throw new Error('Tag is required');
      }
      if (!currentAdjustment.SubTag) {
        throw new Error('SubTag is required');
      }

      // Prepare data for API - ensure all required fields have values
      const adjustmentData = {
        // Don't send ID for new records - let the backend generate UUID
        ...(editMode && currentAdjustment.id ? { id: currentAdjustment.id } : {}),
        AdjustmentId: editMode ? currentAdjustment.AdjustmentId : `ADJ${String(adjustments.length + 1).padStart(3, '0')}`,
        EntityCode: currentAdjustment.EntityCode,
        EntityName: entityName || currentAdjustment.EntityName || '',
        SubEntity: currentAdjustment.SubEntity || '', // Ensure non-null
        AdjustmentCode: currentAdjustment.AdjustmentCode,
        AdjustmentName: adjustmentName || currentAdjustment.AdjustmentName || '',
        ActivityType: currentAdjustment.ActivityType || '', // Ensure non-null
        Amount: parseFloat(currentAdjustment.Amount), // Convert to number
        USDAmount: currentAdjustment.USDAmount ? parseFloat(currentAdjustment.USDAmount) : null,
        Tag: currentAdjustment.Tag || '', // Ensure non-null
        SubTag: currentAdjustment.SubTag || '', // Ensure non-null
        AdjustmentType: currentAdjustment.AdjustmentType,
        ScenarioCode: currentAdjustment.ScenarioCode || (selectedScenario ? selectedScenario.scenarioCode : 'SC001'), // Include scenario code
        PointInTime: currentAdjustment.PointInTime || (selectedScenario ? selectedScenario.PointInTime : null), // Ensure PointInTime code is included
        FilingCode: currentAdjustment.FilingCode || null,
        modifiedBy: currentAdjustment.ModifiedBy || 'admin', // Match backend field name case
        ModifiedOn: new Date().toISOString() // Use ISO format for dates
      };

      // Get the proper route for the adjustment type
      const adjustmentType = currentAdjustment.AdjustmentType;
      const routePath = adjustmentTypeToRouteMap[adjustmentType];

      if (!routePath) {
        throw new Error(`No route configured for adjustment type: ${adjustmentType}`);
      }






      // Save to backend using the correct API endpoint
      if (editMode) {
        // Update existing adjustment
        await api.put(`/adjustments/${routePath}/${currentAdjustment.id}`, adjustmentData);
        const updatedAdjustments = adjustments.map((adj) =>
        adj.id === currentAdjustment.id ? {
          ...adj, // Keep existing fields
          ...adjustmentData, // Update with new data
          // Ensure these fields are always set correctly
          EntityName: entityName || adjustmentData.EntityName || adj.EntityName || '',
          AdjustmentName: adjustmentName || adjustmentData.AdjustmentName || adj.AdjustmentName || '',
          USDAmount: adjustmentData.USDAmount || adj.USDAmount || ''
        } : adj
        );
        setAdjustments(updatedAdjustments);
      } else {
        // Add new adjustment(s) based on entity's boolean fields
        const entity = filteredEntities.find((e) => e.EntityCode === currentAdjustment.EntityCode);

        const subEntities = ['Base'];
        if (entity) {
          if (entity.IsTopSide) subEntities.push('topside');
          if (entity.IsTopSideUS) subEntities.push('topside us');
          if (entity.IsBranch) subEntities.push('branch');
        }

        const newAdjustments = [];
        for (const sub of subEntities) {
          const adjData = {
            ...adjustmentData,
            SubEntity: sub
          };
          try {

            const response = await api.post(`/adjustments/${routePath}`, adjData);

            const savedAdjustment = response.data && response.data.data ?
            {
              ...adjData,
              id: response.data.data.id,
              EntityName: entityName || adjData.EntityName || '',
              AdjustmentName: adjustmentName || adjData.AdjustmentName || '',
              USDAmount: adjData.USDAmount || ''
            } :
            {
              ...adjData,
              EntityName: entityName || adjData.EntityName || '',
              AdjustmentName: adjustmentName || adjData.AdjustmentName || '',
              USDAmount: adjData.USDAmount || ''
            };
            newAdjustments.push(savedAdjustment);
          } catch (err) {
            console.error('DEBUG: Error saving adjustment for sub entity', sub, err);
          }
        }
        setAdjustments([...adjustments, ...newAdjustments]);
      }

      setOpenDialog(false);
      setNotification({
        open: true,
        message: `Adjustment ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });

      // Refresh the adjustment list to ensure we have the latest data
      fetchAdjustments();
    } catch (error) {
      console.error('Error saving adjustment:', error);
      let errorMessage = `Failed to ${editMode ? 'update' : 'add'} adjustment`;

      // Extract more specific error message if available
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage += `: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      setNotification({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
      // Keep the dialog open on error so the user can fix the issue
    }
  };

  // Function to export adjustments data to Excel
  const exportToExcel = () => {
    try {
      // Determine which adjustments to export based on selection
      const adjustmentsToExport = selectedRows.length > 0 ?
      adjustments.filter((adjustment) => selectedRows.includes(adjustment.id)) :
      adjustments;

      // Create a mapping of field names to display names
      const columnMap = {};
      columns.forEach((col) => {
        // Skip action columns, columns without a field property, and columns explicitly marked to disable export
        if (col.field && !col.disableExport && col.field !== 'actions') {
          columnMap[col.field] = col.headerName || col.field;
        }
      });

      // Map the data using the current columns
      const exportData = adjustmentsToExport.map((adjustment) => {
        const row = {};
        Object.keys(columnMap).forEach((field) => {
          // Handle nested properties if needed
          const value = field.split('.').reduce((obj, key) => obj && obj[key] !== undefined ? obj[key] : '', adjustment);
          row[columnMap[field]] = value !== undefined ? value : '';
        });
        return row;
      });

      // Create a worksheet from the exported data
      // Always include headers even if no data
      const headers = Object.values(columnMap);
      let worksheet;

      if (exportData.length === 0) {
        // Create empty template with just headers
        worksheet = XLSX.utils.aoa_to_sheet([headers]);
      } else {
        worksheet = XLSX.utils.json_to_sheet(exportData, {
          header: headers
        });
      }

      // Auto-size columns
      const columnWidths = [];
      const sheetHeaders = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];

      // Calculate max width for each column
      headers.forEach((header, index) => {
        let maxLength = String(header).length;
        exportData.forEach((row) => {
          const value = row[header];
          if (value !== null && value !== undefined) {
            const length = String(value).length;
            if (length > maxLength) maxLength = length;
          }
        });
        // Add some padding
        columnWidths.push({ wch: Math.min(Math.max(maxLength, 10), 50) });
      });

      worksheet['!cols'] = columnWidths;

      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Adjustments');

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Save the file
      saveAs(data, `Adjustments_Export_${new Date().toISOString().slice(0, 10)}.xlsx`);

      setNotification({
        open: true,
        message: adjustmentsToExport.length === 0 ?
        'Empty template exported successfully' :
        `${adjustmentsToExport.length} adjustment(s) exported successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting adjustments:', error);
      setNotification({
        open: true,
        message: 'Failed to export adjustments',
        severity: 'error'
      });
    }
  };


  // Columns are dynamically loaded based on adjustment type in fetchAdjustments

  // Handle notification close
  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  return (
    <Box className="adjustment-inputs-container">
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>

        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Box className="main-content">
        {/* Header Container with Toolbar */}
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title">Input Adjustments</Typography>
            {selectedRows.length > 0 &&
            <Typography variant="body2" className="selection-info" sx={{ ml: 2, color: 'text.secondary' }}>
                {selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
            }
          </Box>
          <Box className="button-group">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              className="action-button"
              onClick={exportToExcel}>

              Export
            </Button>
                        <Button
              variant="outlined"
              size="small"
              startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              className="action-button"
              onClick={handleImportClick}
              disabled={importing}>

              {importing ? 'Importing...' : 'Import'}
            </Button>
            {/* Hidden file input for Excel import */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".xlsx, .xls" />

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              className="action-button">

              Add
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0 || deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
              className="action-button"
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4
                }
              }}>

              {deleting ? 'Deleting...' : 'Remove'}
            </Button>
          </Box>
        </Box>
        
        {/* Data Table */}
        <Box className="table-container">
          {loading ?
          <LinearProgress /> :

          <SearchableDataTable
            searchPlaceholder={selectedAdjustmentType === 'All' ? 'Search Adjustments...' : `Search ${selectedAdjustmentType} adjustments...`}
            rows={adjustments}
            columns={columns}
            loading={loading}
            pageSize={15}
            tableName="adjustments-inputs-table"
            defaultSortField="AdjustmentName"
            defaultSortOrder="asc"
            onRefresh={fetchAdjustments}
            checkboxSelection={true}
            selectionModel={selectedRows}
            onSelectionModelChange={handleSelectionChange}
            height="100%"
            className="custom-datagrid" />

          }
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog
        open={openDialog}
        onClose={handleRequestClose}
        maxWidth="md"
        fullWidth>

        <DialogTitle>{editMode ? 'Edit Adjustment Input' : 'Add New Adjustment Input'}</DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={filteredEntities.
                map((e) => `${e.EntityCode} - ${e.EntityName}`).
                sort((a, b) => a.localeCompare(b))}
                value={currentAdjustment.EntityCode && currentAdjustment.EntityName ? `${currentAdjustment.EntityCode} - ${currentAdjustment.EntityName}` : ''}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const parts = newValue.split(' - ');
                    if (parts.length >= 2) {
                      const code = parts[0];
                      const name = parts.slice(1).join(' - '); // Handle names with dashes
                      const recalc = recalculateAmounts(code, currentAdjustment.ActivityType, currentAdjustment.Amount, currentAdjustment.USDAmount, currentAdjustment.AdjustmentType);
                      setCurrentAdjustment((prev) => ({
                        ...prev,
                        EntityCode: code,
                        EntityName: name,
                        ...recalc
                      }));
                      setEntityName(name);
                    } else {
                      // If format doesn't match, try to find by code or name
                      const entity = filteredEntities.find((e) =>
                      e.EntityCode === newValue || e.EntityName === newValue
                      );
                      if (entity) {
                        const recalc = recalculateAmounts(entity.EntityCode, currentAdjustment.ActivityType, currentAdjustment.Amount, currentAdjustment.USDAmount, currentAdjustment.AdjustmentType);
                        setCurrentAdjustment((prev) => ({
                          ...prev,
                          EntityCode: entity.EntityCode,
                          EntityName: entity.EntityName,
                          ...recalc
                        }));
                        setEntityName(entity.EntityName);
                      }
                    }
                  } else {
                    setCurrentAdjustment((prev) => ({
                      ...prev,
                      EntityCode: '',
                      EntityName: ''
                    }));
                    setEntityName('');
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  // Custom filter to search both entity code and name
                  return options.filter((option) =>
                  option.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  label="Entity (Code - Name)"
                  fullWidth
                  required
                  autoComplete="off" />

                } />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sub Entity"
                value="Base"
                disabled
                autoComplete="off" />

            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={
                (() => {
                  // Filter by scenario's PointInTime first
                  const scenarioPointInTime = selectedScenario?.PointInTime;
                  let filtered = scenarioPointInTime ?
                  masterAdjustments.filter((a) => a.PointInTime === scenarioPointInTime) :
                  masterAdjustments;

                  // Then filter by adjustment type if selected
                  if (selectedAdjustmentType && selectedAdjustmentType !== 'All') {
                    filtered = filtered.filter((a) => a.AdjustmentType === selectedAdjustmentType);
                  }

                  // Deduplicate by AdjustmentCode (should be unique after PointInTime filter, but just in case)
                  const seen = new Set();
                  const unique = filtered.filter((a) => {
                    if (seen.has(a.AdjustmentCode)) {
                      return false;
                    }
                    seen.add(a.AdjustmentCode);
                    return true;
                  });

                  // Map to display format and sort
                  return unique.map((a) => `${a.AdjustmentCode} - ${a.AdjustmentName}`).sort((a, b) => a.localeCompare(b));
                })()
                }
                value={currentAdjustment.AdjustmentCode && currentAdjustment.AdjustmentName ? `${currentAdjustment.AdjustmentCode} - ${currentAdjustment.AdjustmentName}` : ''}
                onChange={(event, newValue) => {
                  if (newValue) {
                    const parts = newValue.split(' - ');
                    if (parts.length >= 2) {
                      const code = parts[0];
                      const name = parts.slice(1).join(' - '); // Handle names with dashes
                      const adj = masterAdjustments.find((a) => a.AdjustmentCode === code);
                      setCurrentAdjustment((prev) => ({
                        ...prev,
                        AdjustmentCode: code,
                        AdjustmentName: name,
                        AdjustmentType: adj ? adj.AdjustmentType : ''
                      }));
                      setAdjustmentName(name);
                    } else {
                      // If format doesn't match, try to find by code or name
                      const adjustment = masterAdjustments.find((a) =>
                      a.AdjustmentCode === newValue || a.AdjustmentName === newValue
                      );
                      if (adjustment) {
                        setCurrentAdjustment((prev) => ({
                          ...prev,
                          AdjustmentCode: adjustment.AdjustmentCode,
                          AdjustmentName: adjustment.AdjustmentName,
                          AdjustmentType: adjustment.AdjustmentType
                        }));
                        setAdjustmentName(adjustment.AdjustmentName);
                      }
                    }
                  } else {
                    setCurrentAdjustment((prev) => ({
                      ...prev,
                      AdjustmentCode: '',
                      AdjustmentName: '',
                      AdjustmentType: ''
                    }));
                    setAdjustmentName('');
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  // Custom filter to search both adjustment code and name
                  return options.filter((option) =>
                  option.toLowerCase().includes(inputValue.toLowerCase())
                  );
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  label="Adjustment (Code - Name)"
                  fullWidth
                  required
                  helperText="Type to search by adjustment code or name"
                  autoComplete="off" />

                } />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="activity-type-label">Activity Type</InputLabel>
                <Select
                  labelId="activity-type-label"
                  value={currentAdjustment.ActivityType || ''}
                  label="Activity Type"
                  onChange={(e) => {
                    const newActivityType = e.target.value;
                    const recalc = recalculateAmounts(currentAdjustment.EntityCode, newActivityType, currentAdjustment.Amount, currentAdjustment.USDAmount, currentAdjustment.AdjustmentType);
                    setCurrentAdjustment({ ...currentAdjustment, ActivityType: newActivityType, ...recalc });
                  }}
                  required
                  disabled={!currentAdjustment.AdjustmentType}>

                  {getActivityTypeOptions(currentAdjustment.AdjustmentType).map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Local Currency Amount"
                type="text"
                autoComplete="off"
                value={formatNumberWithCommas(currentAdjustment.Amount)}
                onChange={(e) => handleAmountChange(e.target.value)}
                required
                helperText={(() => {
                  const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
                  const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
                  if (isPretax) {
                    return 'Pretax adjustments: Manual entry only (no auto-conversion)';
                  }
                  if (!currentAdjustment.EntityCode) return 'Select entity to enable auto-conversion';
                  if (!currentAdjustment.ActivityType) return 'Select activity type to enable auto-conversion';
                  return fxRate ? `FX Rate: ${fxRate.toFixed(6)} (USD auto-calculated)` : 'No FX rate found for this entity/scenario';
                })()} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="USD Amount"
                type="text"
                autoComplete="off"
                value={formatNumberWithCommas(currentAdjustment.USDAmount)}
                onChange={(e) => handleUSDAmountChange(e.target.value)}
                helperText={(() => {
                  const fxRate = getFXRateForEntity(currentAdjustment.EntityCode, currentAdjustment.ActivityType);
                  const isPretax = currentAdjustment.AdjustmentType === 'Pretax' || currentAdjustment.AdjustmentType === 'PretaxAdjustment';
                  if (isPretax) {
                    return 'Pretax adjustments: Manual entry only (no auto-conversion)';
                  }
                  if (!currentAdjustment.EntityCode) return 'Select entity to enable auto-conversion';
                  if (!currentAdjustment.ActivityType) return 'Select activity type to enable auto-conversion';
                  return fxRate ? `FX Rate: ${fxRate.toFixed(6)} (Local auto-calculated)` : 'No FX rate found for this entity/scenario';
                })()} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="tag-label">Tag</InputLabel>
                <Select
                  labelId="tag-label"
                  value={currentAdjustment.Tag || ''}
                  label="Tag"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, Tag: e.target.value })}>

                  {TAG_OPTIONS.map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="subtag-label">Sub Tag</InputLabel>
                <Select
                  labelId="subtag-label"
                  value={currentAdjustment.SubTag || ''}
                  label="Sub Tag"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, SubTag: e.target.value })}>

                  {SUBTAG_OPTIONS.map((option) =>
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            {STATE_ADJUSTMENT_TYPES.includes(currentAdjustment.AdjustmentType) &&
            <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="filing-code-label">Filing Code</InputLabel>
                  <Select
                  labelId="filing-code-label"
                  value={currentAdjustment.FilingCode || ''}
                  label="Filing Code"
                  onChange={(e) => setCurrentAdjustment({ ...currentAdjustment, FilingCode: e.target.value })}
                  disabled={!currentAdjustment.EntityCode}>

                    <MenuItem value=""><em>None</em></MenuItem>
                    {stateInputsForFiling.map((si) =>
                  <MenuItem key={si.id || si.FilingCode} value={si.FilingCode}>
                        {si.FilingCode}{si.StateCode ? ` (${si.StateCode})` : ''}
                      </MenuItem>
                  )}
                  </Select>
                  {!currentAdjustment.EntityCode &&
                <FormHelperText>Select an entity first</FormHelperText>
                }
                </FormControl>
              </Grid>
            }
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Adjustment Type"
                value={currentAdjustment.AdjustmentType || ''}
                autoComplete="off"
                InputProps={{
                  readOnly: true
                }}
                disabled={!currentAdjustment.AdjustmentCode}
                helperText={
                selectedAdjustmentType === 'All' ?
                'Automatically determined by adjustment code' :
                'Controlled by adjustment type filter above'
                } />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Scenario Code"
                value={currentAdjustment.ScenarioCode || (selectedScenario ? selectedScenario.scenarioCode : 'SC001')}
                autoComplete="off"
                InputProps={{
                  readOnly: true
                }}
                disabled
                helperText="Controlled by scenario filter above" />

            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'none' }}>
              <TextField
                fullWidth
                label="Point In Time"
                autoComplete="off"
                value={(() => {
                  // Display the PointInTime name instead of code for better UX
                  const pointInTimeCode = currentAdjustment.PointInTime || '';
                  const pointInTime = pointInTimes.find((pit) => pit.Code === pointInTimeCode);
                  return pointInTime ? pointInTime.Name : pointInTimeCode;
                })()}
                InputProps={{
                  readOnly: true
                }}
                disabled
                helperText="Automatically set from selected scenario" />

            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={!currentAdjustment.EntityCode || !currentAdjustment.AdjustmentCode || !currentAdjustment.Amount || !currentAdjustment.ActivityType || !currentAdjustment.Tag || !currentAdjustment.SubTag}>

            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>);

};

export default AdjustmentInputs;
